import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PostsProvider } from './context/PostsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import UserManagement from './pages/UserManagement';
import Profile from './pages/Profile';
import Unauthorized from './pages/Unauthorized';
// import Games from './pages/Games';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminActivityLog from './pages/AdminActivityLog';
import AdminLeads from './pages/AdminLeads';
import Polls from './pages/Polls';
import HR from './pages/departments/HR';
import IT from './pages/departments/IT';
import Finance from './pages/departments/Finance';
import Sales from './pages/departments/Sales';
import Service from './pages/departments/Service';
import Marketing from './pages/departments/Marketing';
import Admin from './pages/departments/Admin';
import SpareParts from './pages/departments/SpareParts';
import Footer from './components/Layout/Footer';
import './App.css';

// Redirect to dashboard if already logged in
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignUp />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/polls"
        element={
          <ProtectedRoute>
            <Polls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'CO_ADMIN']}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      {/* <Route
        path="/games"
        element={
          <ProtectedRoute>
            <Games />
          </ProtectedRoute>
        }
      /> */}
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminLeads />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/activity"
        element={
          <ProtectedRoute allowedRoles={['ADMIN']}>
            <AdminActivityLog />
          </ProtectedRoute>
        }
      />
      {/* Department routes */}
      <Route
        path="/departments/hr"
        element={
          <ProtectedRoute>
            <HR />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/it"
        element={
          <ProtectedRoute>
            <IT />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/finance"
        element={
          <ProtectedRoute>
            <Finance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/sales"
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/service"
        element={
          <ProtectedRoute>
            <Service />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/marketing"
        element={
          <ProtectedRoute>
            <Marketing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/departments/spareparts"
        element={
          <ProtectedRoute>
            <SpareParts />
          </ProtectedRoute>
        }
      />
      {/* 403 page */}
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const showGlobalFooter = location.pathname !== '/login';

  return (
    <div className="App">
      <AppRoutes />
      {showGlobalFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <PostsProvider>
          <AppContent />
        </PostsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
